'use client'

import * as React from 'react'
import { cn } from '@/utilities/ui'

// The one loading style for every button: the button stays full-size and fully visible, is
// disabled, and swaps its label for a small spinner plus a short "-ing…" label.
const VARIANT = {
  primary: { className: 'btn-primary', spinner: 'border-white' },
  secondary: { className: 'btn-secondary', spinner: 'border-brand-black' },
  red: { className: 'btn-primary-red', spinner: 'border-white' },
} as const

export type LoadingButtonVariant = keyof typeof VARIANT

export function Spinner({ variant = 'primary' }: { variant?: LoadingButtonVariant }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent',
        VARIANT[variant].spinner,
      )}
    />
  )
}

export interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean
  /** Shown next to the spinner while loading, e.g. "Sending…". */
  loadingText: string
  variant?: LoadingButtonVariant
}

export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      loading = false,
      loadingText,
      variant = 'primary',
      className,
      disabled,
      children,
      type,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading}
      className={cn(
        VARIANT[variant].className,
        'gap-2',
        className,
        loading && 'cursor-wait disabled:opacity-100',
      )}
      {...props}
    >
      {loading ? (
        <>
          <Spinner variant={variant} />
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  ),
)
LoadingButton.displayName = 'LoadingButton'
